import {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Popover.module.css';

/**
 * Tank Redesign Popover atom — small anchored menu.
 *
 * Used for the Overview action row's `Refresh: 10s` and `Sort: Newest`
 * ghost-button menus. Mirrors the scaffold pattern: a Btn-like trigger
 * and a portal-rendered panel anchored under it. Outside-click and
 * Escape dismiss; ARIA `aria-haspopup="menu"` + `role="menu"`.
 */

function PopoverPanel({ anchorRect, onClose, children, className, align = 'start' }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorRect || !panelRef.current) return;
    const panel = panelRef.current.getBoundingClientRect();
    const margin = 8;
    let top = anchorRect.bottom + 6;
    if (top + panel.height > window.innerHeight - margin) {
      top = Math.max(margin, anchorRect.top - panel.height - 6);
    }
    let left;
    if (align === 'end') left = anchorRect.right - panel.width;
    else if (align === 'center') left = anchorRect.left + (anchorRect.width - panel.width) / 2;
    else left = anchorRect.left;
    left = Math.max(margin, Math.min(left, window.innerWidth - panel.width - margin));
    setPos({ top, left });
  }, [anchorRect, align]);

  useEffect(() => {
    const onPointer = (e) => {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={panelRef}
      role="menu"
      className={clsx(styles.panel, className)}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
    >
      {children}
    </div>,
    document.body,
  );
}

const PopoverItem = forwardRef(function PopoverItem(
  { selected = false, onSelect, children, className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      role="menuitem"
      type="button"
      className={clsx(styles.item, selected && styles.itemSelected, className)}
      onClick={onSelect}
      {...rest}
    >
      {children}
    </button>
  );
});

/**
 * <Popover trigger={<Btn ...>Refresh: 10s</Btn>}>
 *   <Popover.Item selected={value === 1000} onSelect={() => set(1000)}>1s</Popover.Item>
 *   ...
 * </Popover>
 *
 * The trigger element is cloned with click/keyboard handlers wired to
 * open the menu. Children render inside the portal.
 */
export default function Popover({
  trigger,
  align = 'start',
  className,
  children,
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const triggerRef = useRef(null);
  const id = useId();

  const close = useCallback(() => setOpen(false), []);

  const handleOpen = useCallback(() => {
    if (!triggerRef.current) return;
    setRect(triggerRef.current.getBoundingClientRect());
    setOpen(true);
  }, []);

  if (!isValidElement(trigger)) return null;

  const wrappedTrigger = cloneElement(trigger, {
    ref: (node) => {
      triggerRef.current = node;
      const orig = trigger.ref;
      if (typeof orig === 'function') orig(node);
      else if (orig && typeof orig === 'object') orig.current = node;
    },
    onClick: (e) => {
      trigger.props.onClick?.(e);
      if (e.defaultPrevented) return;
      open ? close() : handleOpen();
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': id,
  });

  return (
    <>
      {wrappedTrigger}
      {open && rect && (
        <PopoverPanel anchorRect={rect} onClose={close} className={className} align={align}>
          <div id={id} onClick={(e) => {
            // Auto-close after a menu item is chosen (target has [role=menuitem]).
            if (e.target.closest('[role="menuitem"]')) close();
          }}>
            {children}
          </div>
        </PopoverPanel>
      )}
    </>
  );
}

Popover.Item = PopoverItem;
